import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Input,
  Modal,
  EMOJI_COLOR_CLASSES,
  DEFAULT_EMOJIS,
} from '@/components/ui';
import { SettingsSubPage } from '@/pages/settings/SettingsSubPage';
import { ChangeProfileImage } from '@/components/domain/shared/ChangeProfileImage';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { SelectEmoji } from '@/components/domain/shared/SelectEmoji';
import { ChildListItem } from '@/components/domain/shared/ChildListItem';
import { AddEditChildForm } from '@/pages/onboarding/AddEditChildForm';
import { useAppShell } from '@/hooks';
import {
  householdService,
  childService,
  invitationService,
  profileImageService,
} from '@/services';
import {
  AlreadyInHouseholdError,
  InvitationEmailNotSentError,
} from '@/services/invitation.service';
import { captureEvent } from '@/lib/analytics';
import { toast } from '@/stores';
import { useUserStore } from '@/stores/userStore';
import type { Child } from '@/types';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Step = 'profile' | 'children' | 'members';

interface MemberDraft {
  id: string;
  email: string;
}

/* ------------------------------------------------------------------ */
/*  Progress bar                                                       */
/* ------------------------------------------------------------------ */

function ProgressBar({ step, total }: { step: number; total: number }) {
  const pct = (step / total) * 100;
  return (
    <div className="h-1 bg-muted w-full">
      <div
        className="h-full bg-primary transition-all duration-300"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export function CreateHouseholdFlow() {
  const navigate = useNavigate();
  const { switchHousehold } = useAppShell();

  // YAR-503: an account owns at most ONE household but may join any number
  // (since Jul 21). Someone who only joined — invited as Co-Admin, Editor or
  // Viewer — owns none and may start their own here, keeping every
  // membership they have. Only an account that already OWNS one is sent
  // away. Ownership comes from the membership list, never the active role.
  //
  // Decided ONCE per visit: step 1 creates the household, after which the
  // user owns it — a guard that re-checked would bounce them out of steps 2
  // and 3. If the list can't be read, the form stays: `households-create`
  // refuses a second owned household (409) and the user sees its message.
  // This is UX, not security.
  useEffect(() => {
    const dbUserId = useUserStore.getState().userId;
    if (!dbUserId) return;
    let active = true;
    householdService
      .getMyHouseholds(dbUserId)
      .then((households) => {
        if (active && households.some((h) => h.role === 'PRIMARY')) {
          navigate('/today', { replace: true });
        }
      })
      .catch(() => {
        /* keep the form — the server is the real gate */
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- latched: once per visit
  }, []);

  // Step state
  const [step, setStep] = useState<Step>('profile');

  // Step 1 — Profile
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState(DEFAULT_EMOJIS[0] as string);
  const [emojiColor, setEmojiColor] = useState(
    EMOJI_COLOR_CLASSES[1] as string,
  );
  const [showChangeImage, setShowChangeImage] = useState(false);
  const [showSelectEmoji, setShowSelectEmoji] = useState(false);
  const [creating, setCreating] = useState(false);

  // Household id — set after creation in step 1
  const [householdId, setHouseholdId] = useState<string | null>(null);
  // The profile as last saved, so Back → Continue updates what changed
  // instead of creating a second household (which would 409 and strand the
  // user on step 1).
  const [savedProfile, setSavedProfile] = useState<{
    name: string;
    emoji: string;
    emojiColor: string;
  } | null>(null);

  // Step 2 — Children
  const [children, setChildren] = useState<Child[]>([]);
  const [showAddChild, setShowAddChild] = useState(false);
  const [editChild, setEditChild] = useState<Child | null>(null);
  const [deleteChild, setDeleteChild] = useState<Child | null>(null);

  // Step 3 — Members
  const [members, setMembers] = useState<MemberDraft[]>([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberEmail, setMemberEmail] = useState('');
  const [sending, setSending] = useState(false);

  /* ---------------------------------------------------------------- */
  /*  Step 1 handlers                                                  */
  /* ---------------------------------------------------------------- */

  const handleCreateHousehold = async () => {
    const trimmed = name.trim();
    if (!trimmed || creating) return;
    setCreating(true);

    // Already created in this visit (the user came back from step 2): save
    // what changed and move on.
    if (householdId) {
      const changed =
        savedProfile?.name !== trimmed ||
        savedProfile.emoji !== emoji ||
        savedProfile.emojiColor !== emojiColor;
      try {
        if (changed) {
          await householdService.updateHousehold(householdId, {
            name: trimmed,
            emoji_avatar: emoji,
            emoji_color: emojiColor,
          });
          setSavedProfile({ name: trimmed, emoji, emojiColor });
        }
        setStep('children');
      } catch (err) {
        console.error('Failed to update household:', err);
        toast.error('Could not save the changes — please try again.');
      } finally {
        setCreating(false);
      }
      return;
    }

    try {
      const household = await householdService.createHousehold({
        name: trimmed,
      });
      captureEvent('household_created', { source: 'create_household_flow' });
      setHouseholdId(household.id);
      setSavedProfile({ name: trimmed, emoji: '', emojiColor: '' });
      // households-create only takes a name; the picture is saved after.
      // The household exists either way, so a failure here doesn't block.
      try {
        await householdService.updateHousehold(household.id, {
          emoji_avatar: emoji,
          emoji_color: emojiColor,
        });
        setSavedProfile({ name: trimmed, emoji, emojiColor });
      } catch (err) {
        console.warn('[CreateHouseholdFlow] picture save failed:', err);
      }
      setStep('children');
    } catch (err) {
      console.error('Failed to create household:', err);
      // The 409 means this account already owns a household (one per
      // account). Anything else is a raw client error — never show it.
      toast.error(
        err instanceof AlreadyInHouseholdError
          ? 'You already own a household. An account can own one.'
          : 'Could not create the household — please try again.',
      );
    } finally {
      setCreating(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Step 2 handlers                                                  */
  /* ---------------------------------------------------------------- */

  const handleAddChild = async (formData: {
    name: string;
    emoji: string;
    emojiColor: string;
    grade: string;
    school: string;
    imageFile?: File | null;
  }) => {
    if (!householdId) return;
    const child = await childService.createChild(householdId, {
      name: formData.name,
      emoji_avatar: formData.emoji,
      grade: formData.grade || null,
      school: formData.school || null,
    });
    if (formData.imageFile) {
      try {
        await profileImageService.uploadAvatarImage(
          'child',
          child.id,
          formData.imageFile,
        );
      } catch (err) {
        console.warn('[CreateHouseholdFlow] photo upload failed:', err);
      }
    }
    setShowAddChild(false);
    const updated = await householdService.getChildren(householdId);
    setChildren(updated);
  };

  const handleEditChild = async (formData: {
    name: string;
    emoji: string;
    emojiColor: string;
    grade: string;
    school: string;
  }) => {
    if (!editChild) return;
    await childService.updateChild(editChild.id, {
      name: formData.name,
      emoji_avatar: formData.emoji,
      emoji_color: formData.emojiColor || null,
      grade: formData.grade || null,
      school: formData.school || null,
    });
    setEditChild(null);
    if (householdId) {
      const updated = await householdService.getChildren(householdId);
      setChildren(updated);
    }
  };

  const handleDeleteChild = async () => {
    if (!deleteChild) return;
    await childService.deleteChild(deleteChild.id);
    setDeleteChild(null);
    if (householdId) {
      const updated = await householdService.getChildren(householdId);
      setChildren(updated);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Step 3 handlers                                                  */
  /* ---------------------------------------------------------------- */

  const handleAddMember = () => {
    const trimmed = memberEmail.trim();
    if (!trimmed) return;
    // Avoid duplicates
    if (members.some((m) => m.email === trimmed)) return;
    setMembers((prev) => [
      ...prev,
      { id: crypto.randomUUID(), email: trimmed },
    ]);
    setMemberEmail('');
    setShowAddMember(false);
  };

  const handleRemoveMember = (id: string) => {
    setMembers((prev) => prev.filter((m) => m.id !== id));
  };

  // Land in the new household once the flow ends — never earlier: switching
  // remounts every page (AppShell keys them on the household), which would
  // throw away the steps still to come. `switchHousehold` pins it and drops
  // the previous household's role, so a reload stays here too.
  const finish = async () => {
    if (!householdId) return;
    // Leave the flow first: the switch remounts the routed page, and doing it
    // here would flash an empty step 1 before Today.
    navigate('/today', { replace: true });
    await switchHousehold?.(householdId);
    toast.success('Household created.');
  };

  const handleSendInvites = async () => {
    if (!householdId || sending) return;
    setSending(true);
    let sent = false;
    try {
      await Promise.all(
        members.map((m) =>
          invitationService.createInvitation(householdId, {
            invitee_email: m.email,
          }),
        ),
      );
      sent = true;
    } catch (err) {
      console.error('Failed to send invitations:', err);
      toast.error(
        err instanceof InvitationEmailNotSentError
          ? err.message
          : 'Could not send the invitations — please try again.',
      );
    } finally {
      setSending(false);
    }
    if (sent) await finish();
  };

  const handleSkip = () => {
    void finish();
  };

  /* ---------------------------------------------------------------- */
  /*  Step 1: Change profile image overlay                             */
  /* ---------------------------------------------------------------- */

  if (showSelectEmoji) {
    return (
      <SelectEmoji
        currentEmoji={emoji}
        currentColor={emojiColor}
        onSave={(e, c) => {
          setEmoji(e);
          setEmojiColor(c);
          setShowSelectEmoji(false);
          setShowChangeImage(false);
        }}
        onBack={() => setShowSelectEmoji(false)}
      />
    );
  }

  if (showChangeImage) {
    // Photo upload is intentionally emoji-only here: the household doesn't
    // exist yet (created on Continue). Users can upload a photo from
    // Settings → Household after creation.
    return (
      <ChangeProfileImage
        onBack={() => setShowChangeImage(false)}
        onChooseEmoji={() => setShowSelectEmoji(true)}
        preview={{ emoji, color: emojiColor }}
      />
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Step 2: Add/Edit child overlay                                   */
  /* ---------------------------------------------------------------- */

  if (showAddChild) {
    return (
      <AddEditChildForm
        mode="add"
        onSave={handleAddChild}
        onBack={() => setShowAddChild(false)}
      />
    );
  }

  if (editChild) {
    return (
      <AddEditChildForm
        mode="edit"
        childId={editChild.id}
        initialData={{
          name: editChild.name,
          emoji: editChild.emoji_avatar ?? '',
          emojiColor:
            editChild.emoji_color ?? (EMOJI_COLOR_CLASSES[0] as string),
          grade: editChild.grade ?? '',
          school: editChild.school ?? '',
          imagePath: editChild.avatar_image_path ?? null,
        }}
        onSave={handleEditChild}
        onBack={() => setEditChild(null)}
      />
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Step 3: Add member overlay                                       */
  /* ---------------------------------------------------------------- */

  if (showAddMember) {
    const canAdd = memberEmail.trim().length > 0;
    return (
      <SettingsSubPage
        title="Invite member"
        onBack={() => {
          setMemberEmail('');
          setShowAddMember(false);
        }}
        stickyFooter={
          <Button
            variant="primary"
            fullWidth
            disabled={!canAdd}
            onClick={handleAddMember}
          >
            Add member
          </Button>
        }
      >
        <div className="flex flex-col gap-5 px-5 py-6">
          <Input
            label="Email"
            type="email"
            value={memberEmail}
            onChange={(e) => setMemberEmail(e.target.value)}
            placeholder="email@example.com"
            autoFocus
          />
        </div>
      </SettingsSubPage>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Render steps                                                     */
  /* ---------------------------------------------------------------- */

  const stepNumber = step === 'profile' ? 1 : step === 'children' ? 2 : 3;

  return (
    <SettingsSubPage
      title="Create new household"
      onBack={() => {
        if (step === 'children') setStep('profile');
        else if (step === 'members') setStep('children');
        else navigate(-1);
      }}
      stickyFooter={
        step === 'profile' ? (
          <Button
            variant="primary"
            fullWidth
            disabled={!name.trim() || creating}
            onClick={handleCreateHousehold}
          >
            {creating ? 'Creating...' : 'Continue'}
          </Button>
        ) : step === 'children' ? (
          <Button
            variant="primary"
            fullWidth
            onClick={() => setStep('members')}
          >
            Continue
          </Button>
        ) : (
          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              fullWidth
              disabled={members.length === 0 || sending}
              onClick={handleSendInvites}
            >
              {sending ? 'Sending...' : 'Send invites'}
            </Button>
            <button
              type="button"
              onClick={handleSkip}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-150 cursor-pointer py-1"
            >
              I'll do this later in settings
            </button>
          </div>
        )
      }
    >
      {/* Progress bar */}
      <ProgressBar
        step={stepNumber}
        total={3}
      />

      {/* Step content */}
      <div className="ph-no-capture px-5 py-6">
        {/* -------------------------------------------------------- */}
        {/*  Step 1: Household profile                                */}
        {/* -------------------------------------------------------- */}
        {step === 'profile' && (
          <div className="flex flex-col gap-8">
            <h2 className="text-[28px] leading-8 font-bold text-foreground">
              Create your household&apos;s profile.
            </h2>
            <p className="-mt-5 text-sm text-muted-foreground">
              You&apos;ll be this household&apos;s admin and stay a member of
              the households you&apos;ve joined. An admin can&apos;t leave the
              household they own.
            </p>

            {/* Emoji avatar */}
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => setShowChangeImage(true)}
                className="cursor-pointer rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                aria-label="Tap to change image"
              >
                <ProfileAvatar
                  emoji={emoji}
                  color={emojiColor}
                  size="2xl"
                  className="border-0"
                />
              </button>
              <span className="text-sm text-muted-foreground">
                Tap to change image
              </span>
            </div>

            {/* Name input */}
            <Input
              label="Household name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. The Smith Family"
              autoFocus
            />
          </div>
        )}

        {/* -------------------------------------------------------- */}
        {/*  Step 2: Add children                                     */}
        {/* -------------------------------------------------------- */}
        {step === 'children' && (
          <div className="flex flex-col gap-6">
            <h2 className="text-[28px] leading-8 font-bold text-foreground">
              Let&apos;s add profiles for your children.
            </h2>

            <div className="flex flex-col gap-3">
              {children.map((child) => (
                <ChildListItem
                  key={child.id}
                  name={child.name}
                  emoji={child.emoji_avatar}
                  color={child.emoji_color}
                  imagePath={child.avatar_image_path}
                  grade={child.grade}
                  school={child.school}
                  onEdit={() => setEditChild(child)}
                  onDelete={() => setDeleteChild(child)}
                />
              ))}

              {/* Add child dashed card */}
              <button
                type="button"
                onClick={() => setShowAddChild(true)}
                className="border border-dashed border-border-strong rounded-xl flex items-center justify-center h-16 w-full cursor-pointer"
              >
                <Plus
                  size={16}
                  className="mr-1.5 text-muted-foreground"
                />
                <span className="text-sm font-semibold text-muted-foreground">
                  Add child
                </span>
              </button>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------- */}
        {/*  Step 3: Invite members                                   */}
        {/* -------------------------------------------------------- */}
        {step === 'members' && (
          <div className="flex flex-col gap-6">
            <h2 className="text-[28px] leading-8 font-bold text-foreground">
              Invite members to share the load.
            </h2>

            <div className="flex flex-col gap-3">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between rounded-xl border border-border bg-card p-4"
                >
                  <span className="text-sm font-medium text-foreground truncate">
                    {member.email}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveMember(member.id)}
                    className="inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-danger-light hover:text-danger transition-colors duration-150 cursor-pointer shrink-0"
                    aria-label={`Remove ${member.email}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}

              {/* Invite member dashed card */}
              <button
                type="button"
                onClick={() => {
                  setMemberEmail('');
                  setShowAddMember(true);
                }}
                className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border-strong p-4 text-sm font-medium text-muted-foreground hover:border-primary hover:text-primary transition-colors duration-150 cursor-pointer"
              >
                Invite member
                <Plus size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete child confirmation modal */}
      <Modal
        open={!!deleteChild}
        onClose={() => setDeleteChild(null)}
        title={`Remove ${deleteChild?.name ?? ''}?`}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setDeleteChild(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteChild}
            >
              Remove
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          This will remove them from your household.
        </p>
      </Modal>
    </SettingsSubPage>
  );
}
